// LoadEnvironmentNode — the editor face of the `loadEnvironment` event node
// (ENG-1031). Picks one of the org's environment assets; the runtime hands it
// to applyEnvironment, the same entry point the content-browser card menu uses.
//
// Two conventions it follows deliberately:
//   - Asset list comes live from CustomAssetManager + subscribe, so an upload
//     (or an out-of-band sync + Refresh) shows up without a reload — copied
//     from PlayBodyClipNode, which is this repo's precedent for an asset
//     dropdown on an action node.
//   - An empty catalogue renders a LABELED inert state naming the fix, not an
//     empty dropdown that looks broken (voiceInputState.js's rule: unavailable
//     controls are shown and explained, never hidden).
//
// It stores BOTH the id and the name: the id is what the runtime resolves (the
// stable server identity), and the name is what this node can still display
// after the row is gone, so a dangling reference reads as a named, flagged
// selection instead of a blank select.

import { Handle, Position } from '@xyflow/react'
import { useState, useEffect } from 'react'
import { isFired } from './useFiredFlash'
import { CustomAssetManager } from '../../lib/CustomAssetManager.js'
import {
  environmentRowLabel,
  environmentRowUrl,
  availableEnvironmentFormats,
  describeFormatLookupError,
} from '../../lib/loadEnvironmentNode.js'
import { environmentFiles } from '../../lib/environmentFiles.js'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

// The splat encodings a blueprint can pin (ENG-1070). This is the display list;
// which of them are actually loadable for the SELECTED environment comes from
// the server's files map, and any this environment doesn't have renders as a
// disabled option naming why — never a silent fallback to the current splat.
const KNOWN_FORMATS = [
  { key: 'ply', label: 'PLY (source, full fidelity)' },
  { key: 'spz', label: 'SPZ (compressed)' },
  { key: 'splat', label: 'SPLAT' },
  { key: 'ksplat', label: 'KSPLAT' },
]

const HEADER_BG     = '#0a3028'
const HEADER_ACTIVE = '#10503a'
const ACCENT        = '#40d090'
const BORDER_SEL    = '#40d090'
const BORDER_IDLE   = '#0d4030'

const envRows = () => [...CustomAssetManager.listEnvironmentAssets()]

export default function LoadEnvironmentNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const [rows, setRows] = useState(envRows)

  useEffect(() => {
    setRows(envRows())
    return CustomAssetManager.subscribe(() => setRows(envRows()))
  }, [])

  const selectedId = data.environmentId ?? ''
  const known      = rows.some(r => r.id === selectedId)
  // Dangling: something is selected but the org no longer publishes it. Say so
  // on the node — the runtime will refuse to load it, and this is where the
  // author can see why before firing anything.
  const dangling   = !!selectedId && rows.length > 0 && !known

  // Which splat formats the SELECTED environment actually has, per the server's
  // files map. Fetched when the selection changes; while unknown, non-default
  // format options stay disabled so the node never implies a variant is loadable
  // before the server has confirmed it (ENG-1070 no-silent-fallback rule).
  const selectedRow = known ? rows.find(r => r.id === selectedId) : null
  const rowUrl      = selectedRow ? environmentRowUrl(selectedRow) : null
  const format      = data.format ?? ''
  const [fmtState, setFmtState] = useState({ available: [], loading: false, error: null })

  useEffect(() => {
    if (!rowUrl) { setFmtState({ available: [], loading: false, error: null }); return }
    let alive = true
    setFmtState({ available: [], loading: true, error: null })
    environmentFiles(rowUrl)
      .then((files) => { if (alive) setFmtState({ available: availableEnvironmentFormats(files), loading: false, error: null }) })
      .catch((err) => {
        // Loud in the console AND on the node. The lookup failing is not the
        // same fact as "this environment has no PLY", and the old single
        // "(couldn't read formats)" string could not tell them apart — see
        // describeFormatLookupError.
        console.warn(`[LoadEnvironmentNode] could not read formats for ${rowUrl}:`, err)
        if (alive) setFmtState({ available: [], loading: false, error: describeFormatLookupError(err) })
      })
    return () => { alive = false }
  }, [rowUrl])

  // The pinned format, if any, is unavailable once we know the env's formats and
  // it isn't among them — surfaced as a warning line, matching the dangling-env
  // pattern. Unknown-yet (loading) is not a failure.
  const formatUnavailable =
    !!format && !fmtState.loading && !fmtState.error && !fmtState.available.includes(format)

  const fieldStyle = {
    width: '100%', background: '#020a06', border: `1px solid ${dangling ? '#a04030' : '#0d3020'}`,
    color: dangling ? '#e08060' : '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
    fontFamily: 'var(--m-mono)', outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 230, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🌍</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Load Environment</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      {/* Environment dropdown (fallback when in-environment isn't wired) */}
      <div className="nodrag" style={{ padding: '8px 12px' }} onPointerDown={stop} onMouseDown={stop}>
        {rows.length > 0 ? (
          <select className={nodeControlClass()}
            value={selectedId}
            onChange={e => {
              const id = e.target.value
              const row = rows.find(r => r.id === id)
              data.onChange?.('environmentId', id)
              data.onChange?.('environmentName', row ? environmentRowLabel(row) : '')
            }}
            onMouseDown={stop}
            style={fieldStyle}
          >
            {/* "None" is a real setting, not the absence of one: it is how a
                character boots with no environment without deleting this node.
                Labelled as the choice it is rather than as a prompt to make a
                different one. */}
            <option value="">— none (load no environment) —</option>
            {dangling && (
              <option value={selectedId}>
                {data.environmentName || selectedId} (missing)
              </option>
            )}
            {rows.map(r => (
              <option key={r.id} value={r.id}>{environmentRowLabel(r)}</option>
            ))}
          </select>
        ) : (
          // Loud + inert, with the fix named. Not a dead select.
          <div style={{ color: '#c08050', fontSize: 9, lineHeight: 1.5 }}>
            no environments in this org — drop a .ply / .splat onto the content
            browser to upload one
          </div>
        )}
        {dangling && (
          <div style={{ color: '#e08060', fontSize: 9, marginTop: 4 }}>
            selected environment is no longer in this org — it will not load
          </div>
        )}

        {/* Format override (ENG-1070): pick a stored splat encoding. Only shown
            once a real environment is selected — the formats are per-environment.
            An option the server doesn't list for this env is disabled with the
            reason, never a silent fallback to the current splat. */}
        {known && (
          <div style={{ marginTop: 8 }}>
            <label style={{ color: '#2a5040', fontSize: 8, letterSpacing: 0.4, display: 'block', marginBottom: 3 }}>
              FORMAT
            </label>
            <select className={nodeControlClass()}
              value={format}
              onChange={e => data.onChange?.('format', e.target.value)}
              onMouseDown={stop}
              style={{
                ...fieldStyle,
                border: `1px solid ${formatUnavailable ? '#a04030' : '#0d3020'}`,
                color: formatUnavailable ? '#e08060' : '#60d090',
              }}
            >
              <option value="">Default (environment's current splat)</option>
              {KNOWN_FORMATS.map(({ key, label }) => {
                const has = fmtState.available.includes(key)
                const suffix = has ? '' : fmtState.loading ? ' (checking…)' : fmtState.error ? fmtState.error.suffix : ' (not available)'
                return (
                  <option key={key} value={key} disabled={!has}>
                    {label}{suffix}
                  </option>
                )
              })}
            </select>
            {/* The lookup failed: say what failed and whose problem it is. The
                dropdown stays visible and disabled (voiceInputState's rule) —
                and "Default" still works, since that path needs no files map. */}
            {fmtState.error && (
              <div style={{ color: '#e08060', fontSize: 9, marginTop: 4, lineHeight: 1.5 }}>
                {fmtState.error.detail}
              </div>
            )}
            {formatUnavailable && (
              <div style={{ color: '#e08060', fontSize: 9, marginTop: 4 }}>
                this environment has no {format.toUpperCase()} variant — it will not load (the server lists {fmtState.available.length ? fmtState.available.map(f => f.toUpperCase()).join(', ') : 'only its current splat'})
              </div>
            )}
          </div>
        )}
      </div>

      {/* in-environment data pin (id or name; overrides the dropdown when wired) */}
      <div style={{ padding: '4px 12px 8px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-environment"
          style={{ background: '#e07040', width: 10, height: 10, borderRadius: '50%', border: '2px solid #e0704044' }} />
        <span style={{ color: '#1a4030', fontSize: 9 }}>ENVIRONMENT (id or name — wired overrides dropdown)</span>
      </div>
    </div>
  )
}
